/* BOJOURNAL — ar-view.js
   <ar-view>: "See it on your wall" in augmented reality.
   The button opens a panel first: the work in 3D (model-viewer, loaded only
   then) beside a short guide and the button that opens the camera.
   - iPhone / iPad: AR Quick Look opens the work's USDZ. The file is anchored
     to a wall and its scaling is locked.
   - Android with in-page AR (WebXR): the camera opens inside the page and
     the work can only be placed on a wall, at a fixed size.
   - Other Android phones: Scene Viewer opens the GLB with wall placement
     and no resizing.
   - Anything else (a computer): a code that opens this page on a phone. */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('ar-view')) return;

  const agent = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(agent);
  const hasQuickLook = (() => {
    const a = document.createElement('a');
    return Boolean(a.relList && a.relList.supports && a.relList.supports('ar'));
  })();

  const loadScript = (src) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  class ArView extends B.Component {
    init() {
      this.dialog = this.querySelector('[data-ar-dialog]');
      this.begin = this.querySelector('[data-ar-start]');
      this.phone = (isIOS && hasQuickLook) || isAndroid;
      // The AR viewers are separate apps: they need complete addresses.
      this.glb = new URL(this.dataset.glb, window.location.href).href;
      this.usdz = new URL(this.dataset.usdz, window.location.href).href;
      this.on(this.querySelector('[data-ar-open]'), 'click', () => this.panel(false));
      this.on(this.begin, 'click', () => this.start());
      this.on(this.querySelector('[data-ar-close]'), 'click', () => this.dialog.close());
      this.on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      // Arrived from the code on a computer: bring the button into view.
      if (window.location.hash === '#ar') {
        this.classList.add('is-called');
        window.setTimeout(() => this.scrollIntoView({ block: 'center' }), 400);
      }
      // Sent back by Android because the AR viewer is missing.
      if (window.location.hash === '#ar-unavailable') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        this.panel(true);
      }
    }

    panel(unsupported) {
      const show = (name, visible) => {
        const part = this.querySelector('[data-ar-' + name + ']');
        if (part) part.hidden = !visible;
      };
      show('phone', this.phone && !unsupported);
      show('desktop', !this.phone && !unsupported);
      show('unsupported', unsupported);
      if (this.dialog.showModal) this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      if (this.built) return;
      this.built = true;
      if (!this.phone) this.code();
      this.stage();
      if (isAndroid) this.prepare();
    }

    // In-page AR is ready once the viewer has the model and a session can start.
    inPage() {
      const viewer = this.viewer;
      return Boolean(viewer && viewer.loaded && viewer.canActivateAR);
    }

    // Where the phone offers in-page AR, hold the camera button briefly until
    // it is ready, so the first press takes the wall-only path.
    prepare() {
      const xr = navigator.xr;
      if (!this.begin || !xr || !xr.isSessionSupported) return;
      const release = () => {
        this.begin.disabled = false;
      };
      let tries = 0;
      const poll = () => {
        if (this.inPage() || tries > 40) return release();
        tries += 1;
        window.setTimeout(poll, 200);
      };
      this.begin.disabled = true;
      xr.isSessionSupported('immersive-ar').then((supported) => (supported ? poll() : release()), release);
    }

    start() {
      if (isIOS && hasQuickLook) this.quickLook();
      else if (this.inPage()) this.viewer.activateAR();
      else this.sceneViewer();
    }

    quickLook() {
      // Safari hands a rel="ar" link whose only child is an image to AR
      // Quick Look. allowsContentScaling=0 keeps the work at its true size.
      const link = document.createElement('a');
      link.rel = 'ar';
      link.href = this.usdz + '#allowsContentScaling=0&canonicalWebPageURL=' + encodeURIComponent(this.dataset.link);
      link.style.display = 'none';
      link.appendChild(document.createElement('img'));
      this.appendChild(link);
      link.click();
      link.remove();
    }

    sceneViewer() {
      const params = new URLSearchParams({
        mode: 'ar_preferred',
        resizable: 'false',
        enable_vertical_placement: 'true',
        disable_occlusion: 'true',
        title: this.dataset.title,
        link: this.dataset.link
      });
      const fallback = this.dataset.link + '#ar-unavailable';
      window.location.href =
        'intent://arvr.google.com/scene-viewer/1.2?' + params.toString() +
        '&file=' + encodeURIComponent(this.glb) +
        '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;' +
        'S.browser_fallback_url=' + encodeURIComponent(fallback) + ';end;';
    }

    code() {
      const box = this.querySelector('[data-ar-qr]');
      if (!box) return;
      loadScript(this.dataset.qr).then(() => {
        const qr = window.qrcode(0, 'M');
        qr.addData(this.dataset.link + '#ar');
        qr.make();
        box.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
      }).catch(() => {
        box.hidden = true;
      });
    }

    stage() {
      const stage = this.querySelector('[data-ar-stage]');
      if (!stage) return;
      import(this.dataset.viewer).then(() => {
        const viewer = document.createElement('model-viewer');
        viewer.setAttribute('src', this.glb);
        viewer.setAttribute('alt', this.dataset.title);
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('touch-action', 'pan-y');
        viewer.setAttribute('interaction-prompt', 'none');
        viewer.setAttribute('camera-orbit', '-28deg 82deg auto');
        viewer.setAttribute('min-camera-orbit', '-80deg 40deg auto');
        viewer.setAttribute('max-camera-orbit', '80deg 110deg auto');
        viewer.setAttribute('shadow-intensity', '0.7');
        viewer.setAttribute('shadow-softness', '0.9');
        viewer.setAttribute('exposure', '1');
        if (isAndroid) this.inPageSetup(viewer);
        stage.appendChild(viewer);
        this.viewer = viewer;
      }).catch(() => {
        stage.hidden = true;
      });
    }

    // In-page AR: walls only, true size, and a short hint over the camera.
    inPageSetup(viewer) {
      viewer.setAttribute('ar', '');
      viewer.setAttribute('ar-modes', 'webxr');
      viewer.setAttribute('ar-placement', 'wall');
      viewer.setAttribute('ar-scale', 'fixed');
      const hint = document.createElement('p');
      hint.className = 'ar-view__hint';
      hint.textContent = this.dataset.hintFind;
      viewer.appendChild(hint);
      viewer.addEventListener('ar-status', (event) => {
        const status = event.detail && event.detail.status;
        hint.textContent = status === 'object-placed' ? this.dataset.hintMove : this.dataset.hintFind;
        // In-page AR could not start: hand over to the phone's own viewer.
        if (status === 'failed') this.sceneViewer();
      });
    }
  }

  B.define('ar-view', ArView);
})();
